/**
 * EXL3 trellis quantization — folded, with its provenance recorded.
 *
 * ## The defect this exists for
 *
 * `malaiwah/Qwen3.8-27B-EXL3-K5K6-hydrated` reported **10.8B parameters at
 * 16.00 bits/weight**. Both numbers are the container rather than the model:
 * 20.1 GiB ÷ 2 bytes is 10.79B, so the count was the number of **`I16` words in
 * the file**, and 16.00 was the storage width of the word holding the packed
 * weights. A 27B checkpoint at roughly 5.5 bits, described as an 11B checkpoint
 * at 16.
 *
 * That is the sixth instance of one family — AWQ, GPTQ, MXFP4, NF4 and
 * compressed-tensors `pack-quantized` were the first five — and the family rule
 * is that a packed container's element count is not a parameter count. Reported
 * by the owner as `5822b568`.
 *
 * ## Why this folds, and on what evidence
 *
 * An EXL3 module stores four tensors, and three of them appear to determine the
 * logical matrix without decoding anything:
 *
 *     mlp.down_proj.trellis   [1088, 320, 96]  I16   packed payload
 *     mlp.down_proj.suh       [17408]          F16   input dimension
 *     mlp.down_proj.svh       [5120]           F16   output dimension
 *     mlp.down_proj.mcg       []               I32
 *
 * `suh × svh` looks like the matrix, and the trellis shape appears to be exactly
 * `[in/16, out/16, K]` with `K/16` the bits per weight — so the shape predicts
 * the width and the width predicts the byte count. Measured across every module
 * of all three shards of that checkpoint, **409 of 409 agree**, with no
 * incomplete groups. The recovered figures are 26,023,034,880 parameters at
 * 5.551 bits/weight, the widths land on exactly 5.0 and 6.0 for a repo named
 * `K5K6`, and the single 4.0 outlier is `mtp.fc` — the multi-token-prediction
 * head, an auxiliary tower quantized harder than the main stack.
 *
 * That agreement alone was **not** enough to fold on, and this module shipped
 * once refusing to (reviewer ruling, 2026-08-17). Every one of the five folders
 * above was measured against ground truth, and 409-fold internal agreement is
 * the strongest possible form of a model agreeing with *itself*, which is not
 * agreeing with the format. So it was verified against the reference
 * implementation before the numbers were allowed to count.
 *
 * ## The provenance, which differs between the two figures
 *
 * **Bits per weight is verified verbatim.** `exllamav3/modules/quant/exl3.py`
 * computes it identically:
 *
 *     self.K = trellis.shape[-1] // 16
 *     assert trellis.dtype == torch.int16, "trellis is wrong datatype"
 *     assert len(trellis.shape) == 3, "trellis must have dim = 3"
 *
 * and the project's documentation gives the reason for the `/16` on the other
 * two axes: EXL3 "operates on 16×16 weight tiles, matching the native input size
 * for modern GPU Tensor Cores".
 *
 * **The parameter count is derived, not verbatim.** No assertion in the loader
 * states that `suh` is `in_features` long and `svh` is `out_features` long — it
 * takes both dimensions as constructor arguments and validates only dtypes. What
 * the format documents is their *semantics*: `suh` is "combined **input
 * channel** scales, random sign flips, and Hadamard scaling" and `svh` is
 * "combined **output channel** scales and random sign flips". A per-channel
 * vector has one entry per channel, which is what those words mean.
 *
 * The residual is small and bounded: the one thing the source does not state is
 * *which* vector is which, and the count is `len(suh) × len(svh)` — a product,
 * so **orientation cannot move it**. Upgrading `params` from derived to
 * verbatim means decoding one tensor against exllamav3's own kernel and
 * recovering the count; that is plan §25c and is worth doing, not required to
 * report the number.
 */

import type { InventoryTensor } from "../safetensors/inventory.js";

/** The four suffixes an EXL3 module stores. */
const TRELLIS = ".trellis";
const SUH = ".suh";
const SVH = ".svh";

/**
 * The packing constant: each trellis axis is its matrix axis over 16.
 *
 * Named rather than inlined because it is the load-bearing number in the
 * structural model and the thing an EXL3 revision would most plausibly change.
 * If it ever moves, `estimate` stops reconciling and this module says so instead
 * of quietly reporting a wrong figure.
 */
const TILE = 16;

export interface Exl3Group {
  /** The module prefix, e.g. `model.…layers.0.mlp.down_proj`. */
  readonly prefix: string;
  /** Every physical tensor in the group. */
  readonly parts: readonly InventoryTensor[];
  /** The packed payload. */
  readonly payload: InventoryTensor;
  /**
   * The recovered reading, when the geometry reconciles.
   *
   * `undefined` when the group is incomplete or the geometry does not come out,
   * which is the fail-closed path and the reason the reconciliation is checked
   * at all: an EXL3 variant this model does not describe must produce **no**
   * number rather than one derived from an assumption that has stopped holding.
   * That is what keeps the verified reading from silently outliving the packing
   * it was verified against.
   */
  readonly resolved: Exl3Resolved | undefined;
}

export interface Exl3Resolved {
  /** `len(suh) × len(svh)` — the logical matrix. */
  readonly params: number;
  /** `trellis.shape[-1] / 16`, cross-checked against payload bytes ÷ params. */
  readonly bitsPerWeight: number;
}

/** Product of a shape, with an empty shape being the scalar 1. */
function numel(shape: readonly number[]): number {
  return shape.reduce((a, b) => a * b, 1);
}

/**
 * Read one module's structural estimate, or `undefined` if it does not reconcile.
 *
 * Every condition here is a *refusal* condition. The function's job is not to
 * produce a number wherever it can, but to produce one only where the model this
 * module documents actually holds — so a shape that does not match
 * `[in/16, out/16, K]` yields nothing rather than a best effort.
 */
export function resolveFromGeometry(
  payloadShape: readonly number[],
  payloadBytes: number,
  suhLength: number,
  svhLength: number,
): Exl3Resolved | undefined {
  if (payloadShape.length !== 3) return undefined;
  const [rows, cols, k] = payloadShape;
  if (rows === undefined || cols === undefined || k === undefined) return undefined;
  if (suhLength <= 0 || svhLength <= 0) return undefined;
  // The tiling has to come out exactly. `!==` rather than a tolerance: these are
  // integers from a header, and "nearly a multiple of 16" is not a thing an
  // honest packing does.
  if (rows * TILE !== suhLength || cols * TILE !== svhLength) return undefined;

  const params = suhLength * svhLength;
  const bitsPerWeight = (payloadBytes * 8) / params;
  /*
   * The independent check. `K/16` is what the *shape* says the width is;
   * `bytes × 8 / elements` is what the *file size* says it is. They are derived
   * from different fields and must agree, which is what makes this a
   * reconciliation rather than a restatement.
   */
  if (Math.abs(bitsPerWeight - k / TILE) > 1e-9) return undefined;
  return { params, bitsPerWeight };
}

/**
 * Find every EXL3 module in an inventory.
 *
 * Keyed on `.trellis`, because that is the payload and the one part that cannot
 * be absent from a real group. A `suh`/`svh` pair with no trellis is not an EXL3
 * module in any state we should reason about, and is left alone.
 */
export function findExl3Groups(tensors: readonly InventoryTensor[]): Exl3Group[] {
  const byName = new Map(tensors.map((t) => [t.name, t]));
  const groups: Exl3Group[] = [];

  for (const tensor of tensors) {
    if (!tensor.name.endsWith(TRELLIS)) continue;
    const prefix = tensor.name.slice(0, -TRELLIS.length);
    const suh = byName.get(`${prefix}${SUH}`);
    const svh = byName.get(`${prefix}${SVH}`);

    const parts = [tensor, suh, svh, byName.get(`${prefix}.mcg`)].filter(
      (p): p is InventoryTensor => p !== undefined,
    );

    /*
     * Incomplete groups decline, and this path has no real data behind it.
     *
     * Every module of the one EXL3 checkpoint measured has all of its sidecars,
     * so this branch is exercised **only by a synthetic fixture** — a fabricated
     * negative control, which the reviewer accepted as valid provided it is
     * labelled as one and fails closed. Said here rather than in a commit
     * message, because the person who needs to know is whoever next sees this
     * branch taken against a real repo.
     */
    if (!suh || !svh) {
      groups.push({ prefix, parts, payload: tensor, resolved: undefined });
      continue;
    }

    groups.push({
      prefix,
      parts,
      payload: tensor,
      resolved: resolveFromGeometry(
        tensor.shape,
        tensor.byteLength,
        numel(suh.shape),
        numel(svh.shape),
      ),
    });
  }
  return groups;
}

/**
 * The receipt: what was recovered, and on what evidence.
 *
 * Returns `undefined` when there is no EXL3 here, so the caller leaves the page
 * untouched rather than rendering an empty disclosure.
 *
 * **Provenance travels with the number** (reviewer condition, 2026-08-17). The
 * two figures do not rest on the same class of evidence — the width is verified
 * verbatim against the reference loader, the count is derived from documented
 * per-channel semantics — and a bare "26.0B at 5.55" would imply they do. A
 * reader who wants to know how far to trust a figure should not have to read
 * this file to find out.
 */
export function describeExl3(groups: readonly Exl3Group[]): string | undefined {
  if (groups.length === 0) return undefined;
  const resolved = groups.filter((g) => g.resolved !== undefined);
  const modules = groups.length;

  if (resolved.length === 0) {
    return `EXL3 trellis quantization — ${modules} module${
      modules === 1 ? "" : "s"
    }, stored packed, whose layout does not match the one this reads. The parameter count is not reported rather than reported as the packed word count.`;
  }

  const params = resolved.reduce((n, g) => n + (g.resolved as Exl3Resolved).params, 0);
  const payloadBytes = resolved.reduce((n, g) => n + g.payload.byteLength, 0);
  const bpw = (payloadBytes * 8) / params;

  const partial =
    resolved.length === modules
      ? ""
      : ` ${modules - resolved.length} of ${modules} modules did not reconcile with that layout and are excluded.`;

  return `EXL3 trellis quantization, across ${modules} module${
    modules === 1 ? "" : "s"
  }. Bits per weight is read from the trellis tile width, the same way exllamav3's own loader computes it. The parameter count is the product of the input- and output-channel scale vectors the file stores, which the format documents as one entry per channel — derived rather than decoded, and not affected by which of the two is which.${partial} The weights themselves are not decoded, so there is no distribution for these tensors.`;
}
